const CLASSICAL_PLANETS = new Set([
  'SUN', 'MOON', 'MARS', 'MERCURY', 'JUPITER', 'VENUS', 'SATURN',
]);

const KP_PLANETS = new Set([...Array.from(CLASSICAL_PLANETS), 'RAHU', 'KETU']);

type Cusp = {
  longitude?: number;
  cuspLord?: string;
  sign?: { ruler?: string };
};

type Planet = {
  name?: string;
  longitude?: number;
  bhavaChalitHouse?: number | string | null;
};

type Chart = {
  planets?: Record<string, Planet>;
  houses?: { cusps?: Record<string | number, Cusp> };
};

type KpAnalysis = {
  kpCusps?: Record<string | number, Cusp>;
};

const normalizePlanet = (name: string) => name.trim().toUpperCase();
const normalizeLongitude = (longitude: number) => ((longitude % 360) + 360) % 360;
const cuspAt = (cusps: Record<string | number, Cusp> | undefined, house: number) => cusps?.[house] || cusps?.[String(house)];

const validHouse = (house: unknown): number | null => {
  if (house === null || house === undefined || house === '') return null;
  const number = Number(house);
  return Number.isInteger(number) && number >= 1 && number <= 12 ? number : null;
};

const completeCuspLongitudes = (cusps: Record<string | number, Cusp> | undefined): number[] | null => {
  const longitudes = Array.from({ length: 12 }, (_, index) => cuspAt(cusps, index + 1)?.longitude);
  if (longitudes.some((longitude) => typeof longitude !== 'number' || !Number.isFinite(longitude))) return null;
  const normalized = (longitudes as number[]).map(normalizeLongitude);
  const arcs = normalized.map((start, index) => (normalized[(index + 1) % 12] - start + 360) % 360);
  if (arcs.some((arc) => arc <= 0) || Math.abs(arcs.reduce((sum, arc) => sum + arc, 0) - 360) > 0.001) return null;
  return normalized;
};

const occupiedHouseFromCusps = (longitude: unknown, cuspLongitudes: number[] | null): number | null => {
  if (typeof longitude !== 'number' || !Number.isFinite(longitude) || !cuspLongitudes) return null;
  const position = normalizeLongitude(longitude);
  for (let index = 0; index < 12; index++) {
    const start = cuspLongitudes[index];
    const end = cuspLongitudes[(index + 1) % 12];
    if (end > start ? position >= start && position < end : position >= start || position < end) return index + 1;
  }
  return null;
};

/** House numbers in this result are actual KP houses; the table rotates them only when displaying. */
export const buildKpHouseTable = (chart: Chart | null | undefined, kp: KpAnalysis | null | undefined) => {
  const chartCusps = chart?.houses?.cusps;
  const kpCusps = kp?.kpCusps;
  const cuspLongitudes = completeCuspLongitudes(kpCusps) || completeCuspLongitudes(chartCusps);
  const ownedByPlanet: Record<string, number[]> = {};
  const occupiedByPlanet: Record<string, number | null> = {};
  const occupantsByHouse: Record<number, string[]> = {};

  for (let house = 1; house <= 12; house++) {
    occupantsByHouse[house] = [];
    const kpLord = cuspAt(kpCusps, house)?.cuspLord;
    const lord = normalizePlanet(kpLord && kpLord !== '-' ? kpLord : (cuspAt(chartCusps, house)?.sign?.ruler || ''));
    if (CLASSICAL_PLANETS.has(lord)) (ownedByPlanet[lord] ||= []).push(house);
  }

  for (const [key, planet] of Object.entries(chart?.planets || {})) {
    const name = normalizePlanet(planet?.name || key);
    if (!KP_PLANETS.has(name)) continue;
    const house = validHouse(planet?.bhavaChalitHouse) ?? occupiedHouseFromCusps(planet?.longitude, cuspLongitudes);
    occupiedByPlanet[name] = house;
    if (house !== null && !occupantsByHouse[house].includes(name)) occupantsByHouse[house].push(name);
  }

  return { ownedByPlanet, occupiedByPlanet, occupantsByHouse };
};

export const toDisplayedKpHouse = (actualHouse: number, rotationHouse: number) =>
  ((actualHouse - rotationHouse + 12) % 12) + 1;
